import React, { useRef, useEffect, useState } from 'react';
import './LandingPage.css';
import { CheckCircle, Cloud, Activity, ShieldCheck, Globe, Zap, Rocket, Shield, Network, Code2, Link2, FileSearch, Layers, RefreshCw, AlertCircle } from "lucide-react";
import AnimatedLogo from '../components/AnimatedLogo';
import Hyperspeed from '../components/Hyperspeed';
import { ParticleCard, GlobalSpotlight } from '../components/MagicBento';
import MigrationDiagram from '../components/MigrationDiagram';
import BorderGlow from '../components/BorderGlow';
import awsLogo from '../assets/logos/aws-logo.svg';
import gcpLogo from '../images/gcp-logo.png';
import azureLogo from '../assets/logos/azure-logo.svg';

/* ─── Live Dashboard Preview Widget ─────────────────────────────────────── */
function DashboardPreview() {
  const [progress, setProgress] = useState(62);
  const [tick, setTick] = useState(0);
  const [history, setHistory] = useState(() => Array.from({ length: 30 }, () => Math.random() * 50 + 20));
  const [latencyHistory, setLatencyHistory] = useState(() => Array.from({ length: 30 }, () => Math.random() * 20 + 30));

  // Slower animation for live graphs
  useEffect(() => {
    const id = setInterval(() => {
      setProgress(p => p >= 78 ? 62 : p + 0.3); // Adjusted increment for slower tick
      setTick(t => {
        const nextT = t + 1;
        // Telemetry spike generator
        const val = Math.random() > 0.85 ? (Math.random() * 40 + 55) : (Math.random() * 25 + 15);
        setHistory(prev => [...prev.slice(1), val]);
        
        // Latency smooth wave + jitter
        const lat = 45 + Math.sin(nextT / 10) * 15 + (Math.random() * 12 - 6);
        setLatencyHistory(prev => [...prev.slice(1), lat]);
        
        return nextT;
      });
    }, 350); // Slowed down significantly (was 60)
    return () => clearInterval(id);
  }, []);

  const phases = [
    { name: 'Schema Translation & Validation', status: 'completed', time: '0m 12s', color: '#34d399' },
    { name: 'Target Infrastructure Provisioning', status: 'completed', time: '1m 45s', color: '#34d399' },
    { name: 'IPSec VPN Tunnel Establishment', status: 'completed', time: '0m 28s', color: '#34d399' },
    { name: 'Initial Snapshot Sync', status: 'in_progress', time: 'Running...', color: '#a78bfa' },
    { name: 'Continuous CDC Replication', status: 'pending', time: 'Queued', color: '#64748b' }
  ];

  return (
    <div className="db-preview-panel">
      {/* Header */}
      <div className="db-preview-header">
        <div className="db-preview-title">
          <span className="db-live-dot" />
          Live Migration Dashboard
        </div>
        <span className="db-preview-badge">AWS RDS → GCP Cloud SQL</span>
      </div>

      {/* Progress bar */}
      <div className="db-section">
        <div className="db-progress-meta">
          <span className="db-label">Overall Progress</span>
          <span className="db-progress-pct">{progress.toFixed(0)}%</span>
        </div>
        <div className="db-progress-track">
          <div className="db-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="db-progress-sub">
          <span>Initial snapshot complete · CDC streaming active</span>
          <span style={{ color: '#34d399' }}>In Progress</span>
        </div>
      </div>

      {/* Stats row */}
      <div className="db-stats-row">
        <div className="db-stat-card">
          <span className="db-stat-value" style={{ color: '#a78bfa' }}>
            {(2_340_000 + tick * 120).toLocaleString()}
          </span>
          <span className="db-stat-label">Rows Synced</span>
        </div>
        <div className="db-stat-card">
          <span className="db-stat-value" style={{ color: '#34d399' }}>
            {(1.8 + Math.sin(tick / 12) * 0.3).toFixed(1)} MB/s
          </span>
          <span className="db-stat-label">Throughput</span>
        </div>
        <div className="db-stat-card">
          <span className="db-stat-value" style={{ color: '#fbbf24' }}>
            {(42 + Math.round(Math.sin(tick / 8) * 5))} ms
          </span>
          <span className="db-stat-label">Lag</span>
        </div>
        <div className="db-stat-card">
          <span className="db-stat-value" style={{ color: '#22d3ee' }}>
            100%
          </span>
          <span className="db-stat-label">Network Uptime</span>
        </div>
      </div>

      {/* Graphical Representation: Dual Charts */}
      <div className="db-section" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        
        {/* Graph 1: Telemetry (Bar) */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="db-label" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
            <span>Telemetry Stream (I/O)</span>
            <span style={{ color: '#a855f7' }}>●</span>
          </div>
          <div className="db-chart-container" style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', padding: '0.4rem 0.5rem', height: '70px' }}>
            {history.map((val, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: `${val}%`,
                  background: val > 65 ? 'linear-gradient(to top, #7c3aed, #a855f7)' : 'linear-gradient(to top, #059669, #34d399)',
                  borderRadius: '1px 1px 0 0',
                  transition: 'height 0.3s ease-out',
                  opacity: 0.85
                }}
              />
            ))}
          </div>
        </div>

        {/* Graph 2: Latency (Area) */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="db-label" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
            <span>Replication Latency (ms)</span>
            <span style={{ color: '#22d3ee' }}>●</span>
          </div>
          <div className="db-chart-container" style={{ height: '70px' }}>
            <svg viewBox="0 0 290 70" width="100%" height="100%" preserveAspectRatio="none">
              <defs>
                <linearGradient id="latency-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.45" />
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.0" />
                </linearGradient>
              </defs>
              <path
                d={`M0,70 L${latencyHistory.map((val, i) => `${i * 10},${70 - (val / 100) * 70}`).join(' ')} L290,70 Z`}
                fill="url(#latency-grad)"
                style={{ transition: 'd 0.3s ease-out' }}
              />
              <polyline
                fill="none"
                stroke="#22d3ee"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                points={latencyHistory.map((val, i) => `${i * 10},${70 - (val / 100) * 70}`).join(' ')}
                style={{ transition: 'points 0.3s ease-out' }}
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="db-preview-footer">
        <span className="db-footer-dot" style={{ background: '#34d399' }} />
        VPN Tunnel Active · IPSec encrypted · 0 errors
      </div>
    </div>
  );
}


function LandingPage({ onStartMigration, onStartDMS, onOpenSettings, theme, toggleTheme }) {
  const featureGridRef = useRef(null);

  // Theme is now forced at the App.js level for the landing page

  const handleOpenLogin = (mode) => {
    window.location.hash = `#/${mode}`;
  };

  return (
    <div className="landing-page">
      <div className="hyperspeed-bg">
        <Hyperspeed />
      </div>
      
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-brand" style={{ display: 'flex', alignItems: 'center' }}>
          <AnimatedLogo />
          <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '15px', alignItems: 'flex-start', justifyContent: 'center' }}>
              <span style={{ fontSize: '1.6rem', fontWeight: '800', color: 'var(--text-main)', letterSpacing: '0.5px', lineHeight: '1.1' }}>Data Lift</span>
              <span className="highlighted-caption">Move Data Faster, Migrate Smarter</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
          <div className="header-ticker-container" style={{ margin: 0 }}>
              <span className="ticker-badge">Info</span>
              <div className="ticker-pulse"></div>
              <div className="ticker-scroll">
                  <span className="ticker-item">
                      Datalift supports secure PostgreSQL migrations between 
                      <span className="ticker-logo-group">
                          <img src={awsLogo} alt="AWS" className="ticker-logo aws-logo-theme-fix" />
                          <span className="ticker-comma">, </span>
                          <img src={azureLogo} alt="Azure" className="ticker-logo" />
                          <span className="ticker-arrow">→</span>
                          <img src={gcpLogo} alt="GCP" className="ticker-logo" />
                      </span>
                  </span>
              </div>
          </div>
          <ParticleCard as="button" className="btn-primary-small" onClick={() => handleOpenLogin('login')} clickEffect={true} enableTilt={false} enableMagnetism={false}>
            Sign In
          </ParticleCard>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Migrate Databases with <span className="gradient-text">Datalift</span>
            </h1>

            <p className="hero-subtitle">
              Seamlessly migrate and replicate databases across clouds with real-time monitoring,
              detailed transaction logging, and enterprise-grade security.
            </p>

            <div className="hero-checklist">
              <div className="checklist-item">
                <CheckCircle size={18} className="checklist-icon" />
                <span>Near-Zero Downtime</span>
              </div>
              <div className="checklist-item">
                <CheckCircle size={18} className="checklist-icon" />
                <span>Real-time Monitoring</span>
              </div>
              <div className="checklist-item">
                <CheckCircle size={18} className="checklist-icon" />
                <span>Automated Workflows</span>
              </div>
            </div>

            <div className="hero-actions">
              <ParticleCard as="button" className="btn-primary-large" onClick={() => handleOpenLogin('register')} clickEffect={true} enableTilt={false} enableMagnetism={false}>
                Get Started
              </ParticleCard>
            </div>
          </div>

          <div className="hero-visual">
            <MigrationDiagram />
          </div>
        </div>
      </section>

      {/* Section 1: Enterprise-grade migration capabilities */}
      <section className="landing-section">
        <div className="section-header-left">
          <span className="badge-pill-feature">
            <ShieldCheck size={14} /> Enterprise-grade migration
          </span>
          <h2 className="section-main-title">
            Migrate your database with <span className="gradient-text">absolute trust.</span>
          </h2>
          <p className="section-sub-desc">
            Replication engineered for high-availability production databases. Stream transactions continuously with near-zero downtime.
          </p>
        </div>

        <div className="compare-section-grid">
          <BorderGlow className="compare-card large-card">
            <div className="compare-card-top">
              <div className="icon-wrap">
                <Activity size={24} />
              </div>
              <h3>Near-Zero Downtime CDC Replication</h3>
              <p>
                Keep databases fully active during migration. Datalift's Change Data Capture (CDC) engine replicates database writes as they occur, avoiding production bottlenecks or locks.
              </p>
            </div>
            <div className="large-card-banner">
              <svg viewBox="15 0 580 185" width="100%" height="100%" style={{ display: 'block' }}>
                <defs>
                  <filter id="neon-glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Grid lines */}
                <g stroke="rgba(255, 255, 255, 0.01)" strokeWidth="1">
                  <line x1="100" y1="0" x2="100" y2="200" />
                  <line x1="200" y1="0" x2="200" y2="200" />
                  <line x1="300" y1="0" x2="300" y2="200" />
                  <line x1="400" y1="0" x2="400" y2="200" />
                  <line x1="500" y1="0" x2="500" y2="200" />
                  <line x1="0" y1="50" x2="600" y2="50" />
                  <line x1="0" y1="100" x2="600" y2="100" />
                  <line x1="0" y1="150" x2="600" y2="150" />
                </g>

                <style>
                  {`
                    @keyframes dashFlow {
                      from { stroke-dashoffset: 40; }
                      to { stroke-dashoffset: 0; }
                    }
                    .flow-cyan { stroke: #22d3ee; animation: dashFlow 2s linear infinite; }
                    .flow-yellow { stroke: #fbbf24; animation: dashFlow 2s linear infinite; }
                    .flow-orange { stroke: #f97316; animation: dashFlow 2s linear infinite; }
                    
                    @keyframes blinkText {
                      0%, 100% { opacity: 0.5; }
                      50% { opacity: 0.95; }
                    }
                    .blink-lbl { animation: blinkText 1.8s ease-in-out infinite; }
                  `}
                </style>

                {/* DML Badge */}
                <rect x="25" y="22" width="46" height="18" rx="9" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
                <text x="48" y="34" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="10.5" fontWeight="800" fontFamily="Outfit,sans-serif">DML</text>

                {/* DML Streams */}
                <g transform="translate(10, 48)">
                  <path d="M 15,10 L 60,10" stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="4 4" className="flow-cyan" />
                  <path d="M 57,7 L 62,10 L 57,13" fill="none" stroke="#22d3ee" strokeWidth="1.2" />
                  <text x="37.5" y="4" textAnchor="middle" fill="#22d3ee" fontSize="9" fontWeight="800" fontFamily="monospace">INSERT</text>

                  <path d="M 15,28 L 60,28" stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="4 4" className="flow-yellow" />
                  <path d="M 57,25 L 62,28 L 57,31" fill="none" stroke="#fbbf24" strokeWidth="1.2" />
                  <text x="37.5" y="22" textAnchor="middle" fill="#fbbf24" fontSize="9" fontWeight="800" fontFamily="monospace">UPDATE</text>

                  <path d="M 15,46 L 60,46" stroke="#f97316" strokeWidth="1.5" strokeDasharray="4 4" className="flow-orange" />
                  <path d="M 57,43 L 62,46 L 57,49" fill="none" stroke="#f97316" strokeWidth="1.2" />
                  <text x="37.5" y="40" textAnchor="middle" fill="#f97316" fontSize="9" fontWeight="800" fontFamily="monospace">DELETE</text>
                </g>

                {/* Databases stack */}
                <g transform="translate(105, 55)">
                  <path d="M -16,-5 C -16,-10 16,-10 16,-5 L 16,15 C 16,20 -16,20 -16,15 Z" fill="rgba(15, 23, 42, 0.9)" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
                  <ellipse cx="0" cy="-5" rx="16" ry="4" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
                  
                  <g transform="translate(12, 10)">
                    <path d="M -16,-5 C -16,-10 16,-10 16,-5 L 16,15 C 16,20 -16,20 -16,15 Z" fill="rgba(15, 23, 42, 0.9)" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
                    <ellipse cx="0" cy="-5" rx="16" ry="4" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
                  </g>

                  <g transform="translate(0, 20)">
                    <path d="M -18,-5 C -18,-11 18,-11 18,-5 L 18,18 C 18,24 -18,24 -18,18 Z" fill="rgba(15, 23, 42, 0.95)" stroke="#818cf8" strokeWidth="1.5" />
                    <ellipse cx="0" cy="-5" rx="18" ry="5" fill="rgba(99,102,241,0.18)" stroke="#818cf8" strokeWidth="1.5" />
                    <line x1="-18" y1="6" x2="18" y2="6" stroke="#818cf8" strokeWidth="0.8" strokeDasharray="3 3" opacity="0.6" />
                  </g>
                </g>

                {/* Arrow to Transaction Logs */}
                <path d="M 105,103 L 105,122" stroke="rgba(255,255,255,0.2)" strokeWidth="1.2" strokeLinecap="round" />
                <path d="M 102,118 L 105,122 L 108,118" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.2" />

                {/* Transaction Logs Icon */}
                <g transform="translate(105, 142)">
                  <rect x="-11" y="-12" width="22" height="24" rx="3" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
                  <path d="M -6,-5 L 6,-5 M -6,0 L 6,0 M -6,5 L 2,5" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
                  <text y="24" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="8.5" fontWeight="600" className="blink-lbl" fontFamily="Outfit,sans-serif">Transaction Logs</text>
                </g>

                {/* Curves from logs up to CDC */}
                <path d="M 120,142 C 170,142 165,100 205,100" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.2" strokeDasharray="2 2" />
                <path d="M 125,75 L 205,75" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" strokeDasharray="3 3" />

                {/* CDC Engine Pill Box */}
                <g transform="translate(205, 60)">
                  <rect x="0" y="0" width="70" height="52" rx="16" fill="rgba(15, 23, 42, 0.95)" stroke="#a78bfa" strokeWidth="1.5" filter="url(#neon-glow)" />
                  <rect x="2" y="2" width="66" height="48" rx="14" fill="rgba(167,139,250,0.05)" />
                  <text x="35" y="26" textAnchor="middle" fill="#a78bfa" fontSize="15" fontWeight="800" letterSpacing="0.5" fontFamily="Outfit,sans-serif">CDC</text>
                  <text x="35" y="38" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="8.5" fontWeight="700" fontFamily="Outfit,sans-serif">CAPTURE</text>
                </g>

                {/* CDC Outputs */}
                <g transform="translate(278, 65)">
                  <path d="M 0,10 L 80,10" stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="4 4" className="flow-cyan" />
                  <path d="M 77,7 L 82,10 L 77,13" fill="none" stroke="#22d3ee" strokeWidth="1.2" />
                  <text x="40" y="5" textAnchor="middle" fill="#22d3ee" fontSize="9" fontWeight="800" fontFamily="monospace">INSERT</text>

                  <path d="M 0,26 L 80,26" stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="4 4" className="flow-yellow" />
                  <path d="M 77,23 L 82,26 L 77,29" fill="none" stroke="#fbbf24" strokeWidth="1.2" />
                  <text x="40" y="21" textAnchor="middle" fill="#fbbf24" fontSize="9" fontWeight="800" fontFamily="monospace">UPDATE</text>

                  <path d="M 0,42 L 80,42" stroke="#f97316" strokeWidth="1.5" strokeDasharray="4 4" className="flow-orange" />
                  <path d="M 77,39 L 82,42 L 77,45" fill="none" stroke="#f97316" strokeWidth="1.2" />
                  <text x="40" y="37" textAnchor="middle" fill="#f97316" fontSize="9" fontWeight="800" fontFamily="monospace">DELETE</text>
                </g>

                {/* Orders Table Grid */}
                <g transform="translate(355, 30)">
                  <text x="117.5" y="-8" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="11" fontWeight="800" letterSpacing="0.8" fontFamily="Outfit,sans-serif">ORDERS TABLE</text>
                  
                  <rect x="0" y="0" width="235" height="142" rx="12" fill="rgba(15, 23, 42, 0.95)" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
                  
                  <g transform="translate(12, 18)" fill="rgba(255,255,255,0.35)" fontSize="8.5" fontWeight="700" fontFamily="Outfit,sans-serif">
                    <text x="0">ID</text>
                    <text x="30">NAME</text>
                    <text x="95">TIMESTAMP</text>
                    <text x="220" textAnchor="end">AMOUNT</text>
                  </g>
                  
                  <line x1="10" y1="26" x2="225" y2="26" stroke="rgba(255,255,255,0.2)" strokeWidth="0.8" />

                  {/* Row 134: INSERT (Cyan Row) */}
                  <g transform="translate(12, 44)" fontSize="9.5" fontWeight="700" fontFamily="monospace">
                    <rect x="-8" y="-11" width="227" height="20" rx="4" fill="rgba(34, 211, 238, 0.04)" />
                    <text x="0" fill="#22d3ee">134</text>
                    <text x="30" fill="#22d3ee" fontFamily="Outfit,sans-serif">Anebi</text>
                    <text x="95" fill="#22d3ee">10/02/26 16:00</text>
                    <text x="220" textAnchor="end" fill="#22d3ee">$20.00</text>
                  </g>

                  {/* Row 133: UPDATE (Yellow Row) */}
                  <g transform="translate(12, 76)" fontSize="9.5" fontWeight="700" fontFamily="monospace">
                    <rect x="-8" y="-11" width="227" height="20" rx="4" fill="rgba(251, 191, 36, 0.04)" />
                    <text x="0" fill="#fbbf24">133</text>
                    <text x="30" fill="#fbbf24" fontFamily="Outfit,sans-serif">Reyna</text>
                    <text x="95" fill="#fbbf24">10/02/26 15:30</text>
                    <text x="220" textAnchor="end" fill="#fbbf24" textDecoration="underline" style={{ textDecorationStyle: 'dotted' }}>$35.00</text>
                  </g>

                  {/* Row 132: DELETE (Orange Strikethrough Row) */}
                  <g transform="translate(12, 108)" fontSize="9.5" fontWeight="700" fontFamily="monospace">
                    <rect x="-8" y="-11" width="227" height="20" rx="4" fill="rgba(249, 115, 22, 0.02)" />
                    <text x="0" fill="#f97316">132</text>
                    <text x="30" fill="#f97316" fontFamily="Outfit,sans-serif">Chris</text>
                    <text x="95" fill="#f97316">10/02/26 15:00</text>
                    <text x="220" textAnchor="end" fill="#f97316">$16.00</text>
                    <line x1="-2" y1="-2" x2="222" y2="-2" stroke="#f97316" strokeWidth="1" opacity="0.8" />
                  </g>
                </g>
              </svg>
            </div>
          </BorderGlow>

          <BorderGlow className="compare-card">
            <div className="compare-card-top">
              <div className="icon-wrap">
                <Network size={24} />
              </div>
              <h3>Cross-Cloud VPN Tunneling</h3>
              <p>
                Security first. Datalift spins up secure cross-cloud VPN tunnels and isolated bastion hosts automatically via Terraform behind the scenes.
              </p>
            </div>
            <div className="card-diagram-banner">
              <svg viewBox="0 0 340 120" width="100%" height="auto" style={{ display: 'block' }}>
                {/* Left VPC */}
                <g transform="translate(10, 22)">
                  <rect x="0" y="0" width="85" height="74" rx="8" fill="rgba(99, 102, 241, 0.02)" stroke="rgba(99, 102, 241, 0.2)" strokeWidth="1" />
                  <text x="42.5" y="-6" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" fontWeight="800" fontFamily="Outfit,sans-serif">AWS/AZURE VPC</text>
                  
                  <g transform="translate(42.5, 25)">
                    <circle r="14" fill="rgba(99, 102, 241, 0.1)" stroke="#818cf8" strokeWidth="1.2" />
                    <path d="M -6,0 L 6,0 M 6,-3 L 9,0 L 6,3 M -6,-3 L -9,0 L -6,3" stroke="#818cf8" strokeWidth="1.2" fill="none" />
                  </g>
                  <text x="42.5" y="52" textAnchor="middle" fill="#ffffff" fontSize="8" fontWeight="800" fontFamily="Outfit,sans-serif">VPN Gateway</text>
                  <text x="42.5" y="64" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="7.5" fontWeight="600" fontFamily="monospace">10.0.0.0/16</text>
                </g>

                {/* Connecting Tunnel Path */}
                <g transform="translate(95, 22)">
                  <rect x="0" y="24" width="150" height="24" rx="12" fill="rgba(167, 139, 250, 0.05)" stroke="rgba(167, 139, 250, 0.2)" strokeWidth="1" />
                  <path d="M 12,36 L 138,36" stroke="#a78bfa" strokeWidth="3" strokeLinecap="round" strokeDasharray="6 8" fill="none" className="tunnel-flow" />
                  <text x="75" y="15" textAnchor="middle" fill="#a78bfa" fontSize="8" fontWeight="800" letterSpacing="0.8" fontFamily="Outfit,sans-serif">IPSec VPN TUNNEL</text>
                  
                  <g transform="translate(75, 36)">
                    <circle r="11" fill="#7c3aed" stroke="#a78bfa" strokeWidth="1" />
                    <path d="M -3.5,-4 L 3.5,-4 L 3.5,0 C 3.5,2.2 0,4.5 0,4.5 C 0,4.5 -3.5,2.2 -3.5,0 Z" fill="none" stroke="#ffffff" strokeWidth="1.1" />
                  </g>
                </g>

                {/* Right VPC */}
                <g transform="translate(245, 22)">
                  <rect x="0" y="0" width="85" height="74" rx="8" fill="rgba(34, 211, 238, 0.02)" stroke="rgba(34, 211, 238, 0.2)" strokeWidth="1" />
                  <text x="42.5" y="-6" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" fontWeight="800" fontFamily="Outfit,sans-serif">GCP VPC</text>
                  
                  <g transform="translate(42.5, 25)">
                    <circle r="14" fill="rgba(34, 211, 238, 0.1)" stroke="#22d3ee" strokeWidth="1.2" />
                    <path d="M -6,0 L 6,0 M 6,-3 L 9,0 L 6,3 M -6,-3 L -9,0 L -6,3" stroke="#22d3ee" strokeWidth="1.2" fill="none" />
                  </g>
                  <text x="42.5" y="52" textAnchor="middle" fill="#ffffff" fontSize="8" fontWeight="800" fontFamily="Outfit,sans-serif">VPN Gateway</text>
                  <text x="42.5" y="64" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="7.5" fontWeight="600" fontFamily="monospace">192.168.0.0/16</text>
                </g>

                <style>
                  {`
                    @keyframes tunnelFlow {
                      from { stroke-dashoffset: 14; }
                      to { stroke-dashoffset: 0; }
                    }
                    .tunnel-flow {
                      animation: tunnelFlow 1.2s linear infinite;
                    }
                  `}
                </style>
              </svg>
            </div>
          </BorderGlow>

          <BorderGlow className="compare-card">
            <div className="compare-card-top">
              <div className="icon-wrap">
                <Code2 size={24} />
              </div>
              <h3>Automated Schema Assessment</h3>
              <p>
                Validate compatibility, translate data types, convert index mappings, and detect conversion errors before starting baseline snapshots.
              </p>
            </div>
            <div className="card-diagram-banner">
              <svg viewBox="0 0 340 120" width="100%" height="auto" style={{ display: 'block' }}>
                {/* Source Types List */}
                <g transform="translate(15, 20)">
                  <rect x="0" y="0" width="90" height="80" rx="8" fill="rgba(255, 255, 255, 0.01)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                  <text x="10" y="14" fill="rgba(255,255,255,0.3)" fontSize="7" fontWeight="800" fontFamily="Outfit,sans-serif">SRC DATATYPES</text>
                  
                  <g transform="translate(10, 32)" fontSize="8" fontWeight="700" fontFamily="monospace">
                    <text y="0" fill="#a5b4fc">varchar(255)</text>
                    <text y="14" fill="#a5b4fc">datetime</text>
                    <text y="28" fill="#a5b4fc">int4</text>
                    <text y="42" fill="#a5b4fc">sysname</text>
                  </g>
                </g>

                {/* Transition mapping lines */}
                <g stroke="rgba(255, 255, 255, 0.12)" strokeWidth="1">
                  <path d="M 115,35 L 225,35" stroke="#fbbf24" strokeWidth="1" strokeDasharray="3 3" />
                  <path d="M 115,49 L 225,49" stroke="#fbbf24" strokeWidth="1" strokeDasharray="3 3" />
                  <path d="M 115,63 L 225,63" stroke="#22d3ee" strokeWidth="1" strokeDasharray="3 3" />
                  <path d="M 115,77 L 225,77" stroke="#34d399" strokeWidth="1" />
                </g>

                {/* Center mapping status badge */}
                <g transform="translate(170, 20)">
                  <rect x="-24" y="-8" width="48" height="15" rx="7.5" fill="#fbbf24" opacity="0.12" stroke="#fbbf24" strokeWidth="0.8" />
                  <text y="2" textAnchor="middle" fill="#fbbf24" fontSize="7" fontWeight="800" fontFamily="Outfit,sans-serif">MAPPING</text>
                </g>

                {/* Converted Types List */}
                <g transform="translate(235, 20)">
                  <rect x="0" y="0" width="90" height="80" rx="8" fill="rgba(255, 255, 255, 0.01)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                  <text x="10" y="14" fill="rgba(255,255,255,0.3)" fontSize="7" fontWeight="800" fontFamily="Outfit,sans-serif">GCP DATATYPES</text>
                  
                  <g transform="translate(10, 32)" fontSize="8" fontWeight="700" fontFamily="monospace">
                    <text y="0" fill="#34d399">text ✓</text>
                    <text y="14" fill="#34d399">timestamp ✓</text>
                    <text y="28" fill="#34d399">integer ✓</text>
                    <text y="42" fill="#34d399">varchar ✓</text>
                  </g>
                </g>
              </svg>
            </div>
          </BorderGlow>
        </div>
      </section>

      {/* Section 2: Replicating data across clouds at hyperspeed */}
      <section className="landing-section">
        <div className="split-layout-section">
          <DashboardPreview />

          <div className="split-left-content">
            <span className="badge-pill-feature">
              <Globe size={14} /> The Datalift Edge
            </span>
            <h2 className="section-main-title">
              Syncing databases <span className="gradient-text">for any cloud.</span>
            </h2>
            <p>
              Migrate databases from AWS RDS or Azure Database to Google Cloud SQL. Automatically configure secure IPSec VPN tunnels and run real-time replication streams seamlessly.
            </p>
            
            <div className="mini-cards-grid">
              <BorderGlow className="mini-card">
                <div className="mini-icon"><Cloud size={16} /></div>
                <span>Multi-Cloud Sync</span>
              </BorderGlow>
              <BorderGlow className="mini-card">
                <div className="mini-icon"><Activity size={16} /></div>
                <span>CDC Streaming</span>
              </BorderGlow>
              <BorderGlow className="mini-card">
                <div className="mini-icon"><ShieldCheck size={16} /></div>
                <span>IPSec Encryption</span>
              </BorderGlow>
              <BorderGlow className="mini-card">
                <div className="mini-icon"><Zap size={16} /></div>
                <span>Sub-second Lag</span>
              </BorderGlow>
            </div>
          </div>
        </div>
      </section>



      {/* Section 3: A beautifully simple four-step workflow */}
      <section className="landing-section">
        <div className="section-header-center">
          <span className="badge-pill-feature">
            <Zap size={14} /> Beautifully Simple
          </span>
          <h2 className="section-main-title">
            A simple <span className="gradient-text">four-step workflow</span>
          </h2>
          <p className="section-sub-desc">
            Migrations don't have to be complicated. Get your pipeline sync'd in minutes.
          </p>
        </div>

        <div className="timeline-container">
          <div className="timeline-line"></div>
          
          <div className="timeline-item">
            <div className="timeline-dot"><Link2 size={16} /></div>
            <BorderGlow className="timeline-card">
              <span className="timeline-step-badge">STEP #1</span>
              <h3>Connect Databases</h3>
              <p>Configure credentials for your source and destination instances safely. Connection details are encrypted at rest.</p>
            </BorderGlow>
          </div>

          <div className="timeline-item">
            <div className="timeline-dot"><FileSearch size={16} /></div>
            <BorderGlow className="timeline-card">
              <span className="timeline-step-badge">STEP #2</span>
              <h3>Assess Compatibility</h3>
              <p>Datalift performs validation, identifying data type conversion mismatch risks or locking hazards in advance.</p>
            </BorderGlow>
          </div>

          <div className="timeline-item">
            <div className="timeline-dot"><Layers size={16} /></div>
            <BorderGlow className="timeline-card">
              <span className="timeline-step-badge">STEP #3</span>
              <h3>Snapshot Bulk Copy</h3>
              <p>Execute parallel table snapshot exports. Baseline copies migrate the core tables without locking active writes.</p>
            </BorderGlow>
          </div>

          <div className="timeline-item">
            <div className="timeline-dot"><RefreshCw size={16} /></div>
            <BorderGlow className="timeline-card">
              <span className="timeline-step-badge">STEP #4</span>
              <h3>Continuous CDC Stream</h3>
              <p>Start transaction streams. Once the replication lag reaches sub-second latency, trigger a safe database cutover.</p>
            </BorderGlow>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Migrate Your Databases?</h2>
          <ParticleCard as="button" className="btn-primary-large" onClick={() => handleOpenLogin('register')} clickEffect={true} enableTilt={false} enableMagnetism={false}>
            Start Your Migration Now
          </ParticleCard>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-left">
              <AnimatedLogo />
              <p className="footer-desc">
                The unified control plane for secure database migrations and real-time multi-cloud database replication.
              </p>
            </div>
            
            <div className="footer-divider"></div>
            
            <div className="footer-right">
              <span className="footer-title">CLOUD AMBASSADORS</span>
              <p className="footer-link-text">
                Visit the <a href="https://canexus.ai/" target="_blank" rel="noopener noreferrer">CA Nexus Innovation Hub</a> to view our other Cloud Solutions.
              </p>
            </div>
          </div>

          <div className="footer-bottom">
            <button 
              className="scroll-to-top-btn" 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              aria-label="Scroll to top"
            >
              ↑
            </button>
            <p className="footer-copyright">&copy; 2026 Cloud Ambassadors. All Rights Reserved.</p>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
    </div>
  );
}

export default LandingPage;